import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, ButtonContainer, Checkbox, FormControlLabel, DatePicker, Link, ScrollableDrawer, Stack, TextField, Typography, useCurrentBreakpoint } from '@emburse/embark-core';
import { SupportIcon } from '@emburse/embark-icons';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import { CurrencySelect, EmployeeSearch, IconPopover } from '../../../components';
import { getCurrenciesSorted, getExpenseCategories } from '../../../api/api';
import { CurrencySortBy, ConvertedMoney } from '../../../interfaces/Money';
import { Allowance } from '../../../interfaces/Allowance';
import { dateAdapterInstance } from '../../../util/dateAdapter';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import useAccountConfig from '../../../hooks/useAccountConfig';
const { formats } = dateAdapterInstance;
function CorpAllowancesDrawer({ loading, selectedAllowance, invalidFields, saveClickHandler, deleteClickHandler, cancelClickHandler, open, anchor, openUpdated }) {
    useTranslation();
    const { accountConfig } = useAccountConfig();
    const currentBreakpoint = useCurrentBreakpoint();
    // Form Fields
    const [allowanceName, setAllowanceName] = useState({ value: '', validationText: '' });
    const [code, setCode] = useState({ value: '', validationText: '' });
    const [amount, setAmount] = useState({ value: '', validationText: '' });
    const [employee, setEmployee] = useState({ value: null, validationText: '' });
    const [active, setActive] = useState({ value: true, validationText: '' });
    const [hardStop, setHardStop] = useState({ value: false, validationText: '' });
    const [categories, setCategories] = useState({ value: [], validationText: '' });
    const [currencyType, setCurrencyType] = useState({
        value: accountConfig.Emp.CurrencyType.PK,
        validationText: ''
    });
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateValidation, setDateValidation] = useState({ startDate: '', endDate: '' });
    const [currencies, setCurrencies] = useState([]);
    const [selectableExpenseCategories, setSelectableExpenseCategories] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    // hack for drawer visibility to show Amount text left-aligned
    const [drawerVisibility, setDrawerVisibility] = useState(false);
    const amountRef = useRef();
    const initialStateRef = useRef(null);
    const onResetState = () => {
        setCategories({ value: [], validationText: '' });
        setAmount({ value: '', validationText: '' });
        setActive({ value: true, validationText: '' });
        setHardStop({ value: false, validationText: '' });
        setEmployee({ value: null, validationText: '' });
        setAllowanceName({ value: '', validationText: '' });
        setCode({ value: '', validationText: '' });
        setStartDate(null);
        setEndDate(null);
        setDateValidation({ startDate: '', endDate: '' });
    };
    useEffect(() => {
        if (!open) {
            setDrawerVisibility(false);
            onResetState();
        }
        else {
            setValidationResults([]);
            const allowance = new Allowance(selectedAllowance);
            const strAmount = allowance.Amount.Value > 0 ? allowance.Amount.Value.toString() : '';
            allowance.Employee = allowance.Employee?.FullName ? allowance.Employee : null;
            setCategories({ value: allowance.ExpCats, validationText: '' });
            setAmount({ value: strAmount, validationText: '' });
            setActive({ value: allowance.Active, validationText: '' });
            setHardStop({ value: allowance.RequiredForSubmission, validationText: '' });
            setEmployee({ value: allowance.Employee, validationText: '' });
            setAllowanceName({ value: allowance.Name, validationText: '' });
            setCode({ value: allowance.Code, validationText: '' });
            setStartDate(allowance.StartDate ? dayjs(allowance.StartDate) : null);
            setEndDate(allowance.EndDate ? dayjs(allowance.EndDate) : null);
            setCurrencyType({
                value: allowance.CurrencyType?.PK || accountConfig.Emp.CurrencyType.PK,
                validationText: ''
            });
            initialStateRef.current = {
                ...allowance,
                Amount: strAmount,
                CurrencyType: allowance.CurrencyType?.PK || accountConfig.Emp.CurrencyType.PK
            };
            delete initialStateRef.current.PK;
            delete initialStateRef.current.id;
            setDrawerVisibility(true);
            // If we've opened the drawer and we have a valid employee selected, load Expense Categories
            if (allowance.Employee?.PK) {
                const requestOptions = {
                    Emp: allowance.Employee
                };
                // pull expense categories based off selected employee
                getExpenseCategories(requestOptions, false).then((data) => setSelectableExpenseCategories(data));
            }
        }
    }, [accountConfig, open, selectedAllowance]);
    useEffect(() => {
        if (invalidFields.length) {
            const setStateHandlerMap = {
                Name: setAllowanceName,
                Amount: setAmount,
                CurrencyType: setCurrencyType,
                Employee: setEmployee,
                StartDate: setStartDate,
                EndDate: setEndDate,
                ExpCats: setCategories
            };
            const newValidationResults = [];
            invalidFields.forEach((item) => {
                if (item.Key) {
                    setStateHandlerMap[item.Key]((prevState) => ({
                        ...prevState,
                        validationText: item.Message
                    }));
                }
                if (item.Message) {
                    newValidationResults.push(item.Message);
                }
            });
            if (newValidationResults.length) {
                setValidationResults(newValidationResults);
            }
        }
    }, [invalidFields]);
    useEffect(() => {
        getCurrenciesSorted(CurrencySortBy.Name).then(setCurrencies);
    }, []);
    useEffect(() => {
        if (drawerVisibility) {
            if (amountRef.current) {
                amountRef.current.style.textAlign = 'left';
            }
        }
    }, [drawerVisibility]);
    const allowanceNameChangeHandler = (e) => {
        setAllowanceName({ validationText: '', value: e.target.value });
    };
    const codeChangeHandler = (e) => {
        setCode({ validationText: '', value: e.target.value });
    };
    const amountChangeHandler = (e) => {
        setAmount({ validationText: '', value: e.target.value });
    };
    const employeeChangeHandler = (newEmployee) => {
        setEmployee({ validationText: '', value: newEmployee });
        if (!newEmployee?.PK) {
            setSelectableExpenseCategories([]);
            setCategories({ value: [], validationText: '' });
        }
        else {
            const requestOptions = {
                Emp: newEmployee
            };
            // pull expense categories based off selected employee
            getExpenseCategories(requestOptions, false).then((data) => setSelectableExpenseCategories(data));
        }
    };
    // ChangeEvent throws an error even though e.target.value is an array
    const categoryChangeHandler = (e, value) => setCategories({ validationText: '', value });
    const startDateChangeHandler = (value) => {
        setStartDate(value);
        setDateValidation((prevState) => ({ ...prevState, startDate: '' }));
    };
    const endDateChangeHandler = (value) => {
        setEndDate(value);
        setDateValidation((prevState) => ({ ...prevState, endDate: '' }));
    };
    const activeChangeHandler = (e) => setActive({ validationText: '', value: e.target.checked });
    const hardStopChangeHandler = (e) => setHardStop({ validationText: '', value: e.target.checked });
    const isEndDateBeforeStartDate = () => dayjs(endDate).isBefore(dayjs(startDate));
    const checkValidation = () => {
        let valid = true;
        const required = [
            [allowanceName.value, setAllowanceName],
            [startDate, setDateValidation],
            [endDate, setDateValidation],
            [amount.value, setAmount],
            [categories.value, setCategories]
        ];
        const newValidationResults = [];
        if (isEndDateBeforeStartDate()) {
            setDateValidation((prevState) => ({
                ...prevState,
                endDate: $t('End date cannot come before start date')
            }));
            newValidationResults.push($t('End date cannot come before start date.'));
            valid = false;
        }
        if (!employee.value?.PK) {
            setEmployee((prevState) => ({
                ...prevState,
                validationText: $t('This field is required')
            }));
            newValidationResults.push($t('Some required fields are empty.'));
            valid = false;
        }
        if ((startDate && !dayjs(startDate).isValid()) || (endDate && !dayjs(endDate).isValid())) {
            newValidationResults.push($t('Some date fields are invalid.'));
            valid = false;
        }
        required.forEach(([value, setter]) => {
            if (!value || value.length === 0) {
                if (!startDate || !endDate) {
                    setter((prevState) => ({
                        ...prevState,
                        startDate: !startDate ? $t('This field is required') : '',
                        endDate: !endDate ? $t('This field is required') : ''
                    }));
                }
                setter((prevState) => ({
                    ...prevState,
                    validationText: $t('This field is required')
                }));
                if (!newValidationResults.includes($t('Some required fields are empty.'))) {
                    newValidationResults.push($t('Some required fields are empty.'));
                }
                valid = false;
            }
        });
        setValidationResults(newValidationResults);
        return valid;
    };
    const onSubmitHandler = () => {
        const isValid = checkValidation();
        if (isValid) {
            const data = new Allowance({
                ...(selectedAllowance?.PK
                    ? {
                        PK: selectedAllowance?.PK,
                        id: selectedAllowance?.id
                    }
                    : {}),
                Name: allowanceName.value,
                Code: code.value,
                Amount: selectedAllowance
                    ? { ...selectedAllowance.Amount, ...{ Value: parseFloat(amount.value) } }
                    : new ConvertedMoney({ Value: parseFloat(amount.value) }),
                Employee: employee.value,
                Active: active.value,
                StartDate: new Date(startDate.toISOString()),
                EndDate: new Date(endDate.toISOString()),
                RequiredForSubmission: hardStop.value,
                CurrencyType: currencies.find((item) => item.PK === currencyType.value),
                // back end asked us to sort by PK
                ExpCats: categories.value.sort((a, b) => a.PK - b.PK)
            });
            saveClickHandler(data);
        }
    };
    const onCancelHandler = () => {
        const currentState = {
            Name: allowanceName.value,
            Code: code.value,
            Amount: amount.value,
            Employee: employee.value,
            Active: active.value,
            StartDate: startDate,
            EndDate: endDate,
            RequiredForSubmission: hardStop.value,
            CurrencyType: currencyType.value,
            ExpCats: categories.value
        };
        const initialState = initialStateRef.current;
        const booIsEqual = isEqual(initialState, currentState);
        cancelClickHandler(booIsEqual);
    };
    return (_jsx(ScrollableDrawer, { anchor: anchor, open: open, sideWidth: currentBreakpoint.current === 'xs' ? 380 : 450, disableCloseOnBackdropClick: true, openUpdated: openUpdated, header: $t('User Allowance'), footer: _jsxs(Stack, { direction: "row", children: [_jsx(ButtonContainer, { sx: { width: '50%' }, children: _jsx(Button, { color: "error", variant: "outlined", disabled: loading || !selectedAllowance?.PK, onClick: deleteClickHandler, children: $t('Delete') }) }), _jsxs(ButtonContainer, { alignButtons: "right", sx: { width: '50%' }, children: [_jsx(Button, { disabled: loading, variant: "outlined", onClick: onCancelHandler, children: $t('Cancel') }), _jsx(Button, { disabled: loading, onClick: onSubmitHandler, children: $t('Save') })] })] }), children: _jsxs(Stack, { spacing: 3, direction: "column", alignItems: "stretch", justifyContent: "space-between", children: [validationResults.length > 0 && (_jsx(Alert, { severity: "error", alertTitle: $t('Please fix the following:'), children: validationResults.map((item) => (_jsx(Typography, { children: item }, `${item.Key}`))) })), _jsx(FormControlLabel, { value: "1", control: _jsx(Checkbox, { id: "ActiveAllowance", value: active.value, onChange: activeChangeHandler, checked: active.value }), label: _jsx(Typography, { children: $t('Active') }) }), _jsx(TextField, { id: "AllowanceName", name: "Allowance Name", fullWidth: true, value: allowanceName.value, label: $t('Allowance Name *'), type: "text", helperText: allowanceName.validationText, error: Boolean(allowanceName.validationText), onChange: allowanceNameChangeHandler }), _jsx(TextField, { id: "Code", name: "Code", fullWidth: true, value: code.value, label: $t('Code'), type: "text", onChange: codeChangeHandler }), _jsx(EmployeeSearch, { id: "UserName", label: $t('User Name *'), value: employee.value, helperText: employee.validationText, error: Boolean(employee.validationText), onSelect: employeeChangeHandler }), _jsx(DatePicker, { label: $t('Start Date *'), value: startDate, datePickerType: currentBreakpoint.current === 'xs' ? 'mobile' : 'desktop', format: formats.keyboardDate, onChange: startDateChangeHandler, sx: {
                        '& .MuiInputLabel-root': {
                            top: '-5px'
                        },
                        '& .MuiInputLabel-shrink': {
                            top: '0px'
                        }
                    }, slotProps: {
                        textField: {
                            id: 'StartDate',
                            name: 'Start Date',
                            fullWidth: true,
                            error: Boolean(dateValidation.startDate),
                            helperText: dateValidation.startDate
                        }
                    } }), _jsx(DatePicker, { label: $t('End Date *'), value: endDate, datePickerType: currentBreakpoint.current === 'xs' ? 'mobile' : 'desktop', format: formats.keyboardDate, onChange: endDateChangeHandler, sx: {
                        '& .MuiInputLabel-root': {
                            top: '-5px'
                        },
                        '& .MuiInputLabel-shrink': {
                            top: '0px'
                        }
                    }, slotProps: {
                        textField: {
                            id: 'EndDate',
                            name: 'End Date',
                            fullWidth: true,
                            error: Boolean(dateValidation.endDate),
                            helperText: dateValidation.endDate
                        }
                    } }), _jsx(TextField, { id: "Amount", name: "Amount", fullWidth: true, value: amount.value, label: $t('Amount *'), type: "number", inputRef: amountRef, helperText: amount.validationText, error: Boolean(amount.validationText), onChange: amountChangeHandler, sx: { width: '100%' } }), _jsx(CurrencySelect, { id: "CurrencyType", style: { width: '100%', minWidth: 'auto' }, label: "", value: currencyType.value, options: currencies, error: !!currencyType.validationText, onSelect: (currency, reason) => {
                        if (reason === 'clear') {
                            setCurrencyType({ validationText: '', value: 121 });
                        }
                        else {
                            setCurrencyType({ validationText: '', value: currency.PK });
                        }
                    } }), _jsx(FormControlLabel, { value: "1", control: _jsx(Checkbox, { id: "HardStop", value: hardStop.value, onChange: hardStopChangeHandler, checked: hardStop.value }), style: { marginLeft: '-9px' }, label: _jsxs(_Fragment, { children: [_jsx(Typography, { display: "inline", children: $t('Prevent Report Submission') }), _jsx(IconPopover, { title: $t('More information'), content: _jsxs(Typography, { variant: "body2", children: [$t(`When the allowance amount is exceeded, prevent this user from submitting
                                        expense reports that apply to this allowance.`), ' ', _jsx(Link, { href: "https://help.certify.com/hc/en-us", target: "_blank", children: $t('Learn more') })] }), iconUrl: SupportIcon, edge: false })] }) }), _jsx(Autocomplete, { id: "Category", multiple: true, multipleType: "chips", label: $t('Category *'), style: { width: '100%' }, value: categories.value, helperText: categories.validationText, error: Boolean(categories.validationText), disabled: !employee.value?.PK, limitTags: 5, disableCloseOnSelect: true, showCheckbox: true, options: selectableExpenseCategories, onChange: categoryChangeHandler, getOptionLabel: (option) => (selectableExpenseCategories.find(({ PK }) => PK === option.PK) &&
                        option.Name) ||
                        'unknown', isOptionEqualToValue: (option, value) => option.PK === value.PK })] }) }));
}
export default CorpAllowancesDrawer;
